import React, { type JSX } from 'react';
import { Box, Typography, Checkbox, List, Stack } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { InfoListItem } from '@brightlayer-ui/react-components';
import { useGetAllLoadsQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { ListSkeleton } from 'pages/WidgetManagement/common/Skeletons';
import type { SelectedLoad } from './AddLoads';
import { useDebounce } from '@fiji/common/src/hooks';
import { CustomTransComponent } from 'components/CustomTransComponent';
import { useNewCustomTranslations } from 'hooks';
import { InputWithCustomPlaceholder } from '@fiji/web/src/components/InputWithCustomPlaceholder';

export const LoadListsHierarchy = (props: any): JSX.Element => {
    const [searchKey, setSearchKey] = React.useState<string>('');
    const [payload, setPayload] = React.useState({
        page: 0,
        size: 25,
        searchKey: '',
    });
    const [, debouncedValue] = useDebounce(undefined, undefined, searchKey);
    React.useEffect(() => {
        setPayload((prev: any) => ({
            ...prev,
            page: 0,
            searchKey: debouncedValue ?? '',
        }));
    }, [debouncedValue]);
    const {
        isLoading,
        isFetching,
        data: loads,
    } = useGetAllLoadsQuery(
        {
            deviceId: props?.selectedDevice?.id,
            body: payload,
        },
        { skip: !props?.selectedDevice?.id }
    );
    const loadNumberStyle = {
        fontSize: '14px',
        fontWeight: '600',
        backgroundColor: '#727E84',
        color: '#fff',
        width: '18px',
        height: '18px',
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };
    useNewCustomTranslations('WIDGETS');
    const getDisplayLoadNumber = (load: SelectedLoad): string => {
        if (load?.loadNumber === undefined || load?.loadNumber === null || load?.loadNumber === '') {
            return '';
        }

        const parsedLoadNumber = Number(load.loadNumber);

        return Number.isNaN(parsedLoadNumber) ? String(load.loadNumber) : String(parsedLoadNumber + 1);
    };

    const { t } = useTranslation();

    const normalizeLoad = (item: any): any => ({
        ...item,
        id: item?.id ?? item?.loadId,
        loadId: item?.loadId ?? item?.id,
        loadName: item?.loadName ?? item?.name ?? '',
        deviceName: item?.deviceName ?? props?.selectedDevice?.name ?? '',
        groupPath: item?.groupPath ?? props?.selectedDevice?.groupPath ?? '',
    });

    const isExistingLoad = (item: any): boolean => {
        const normalizedLoad = normalizeLoad(item);
        const selectedDeviceId = props?.selectedDevice?.id;
        const identifiers = [
            normalizedLoad?.id,
            normalizedLoad?.loadId,
            normalizedLoad?.loadConfigId,
            selectedDeviceId && normalizedLoad?.loadId ? `${selectedDeviceId}_${normalizedLoad.loadId}` : undefined,
            item?.deviceId && normalizedLoad?.loadId ? `${item.deviceId}_${normalizedLoad.loadId}` : undefined,
        ].filter(Boolean);

        return identifiers.some((identifier) => props?.existingLoadIds?.includes(identifier));
    };

    const filteredLoads = React.useMemo(() => {
        const records = loads?.data?.loadConfiguration ?? [];
        const normalizedSearch = String(searchKey ?? '')
            .trim()
            .toLowerCase();

        if (!normalizedSearch) {
            return records;
        }

        return records.filter((item: any) => {
            const loadName = String(item?.loadName ?? item?.name ?? '').toLowerCase();
            const loadNumber = getDisplayLoadNumber(item).toLowerCase();

            return loadName.includes(normalizedSearch) || loadNumber.includes(normalizedSearch);
        });
    }, [loads?.data?.loadConfiguration, searchKey]);

    const handleChannelSelection = (item: any): void => {
        if (isExistingLoad(item)) {
            return;
        }

        const loadIds = JSON.parse(JSON.stringify(props?.createPayload ?? []));
        const normalizedLoad = normalizeLoad(item);

        const index = loadIds.findIndex((subItem: any) => (subItem?.id ?? subItem?.loadId) === normalizedLoad.id);

        if (index !== -1) {
            loadIds.splice(index, 1);
        } else {
            loadIds.push(normalizedLoad);
        }
        props?.setCreatePayload?.(loadIds);
    };

    const handleChecked = (item: any): boolean => {
        const normalizedLoad = normalizeLoad(item);

        return (
            props?.createPayload?.some((subItem: any) => (subItem?.id ?? subItem?.loadId) === normalizedLoad.id) ||
            isExistingLoad(item)
        );
    };

    return (
        <Box display="flex" flexDirection="column">
            <Box sx={{ backgroundColor: '#f2f2f2' }}>
                <Typography variant="h6" gutterBottom fontSize={16} fontWeight={600} color="#424e54">
                    <CustomTransComponent translationKey={'WIDGETS:LOADS.SELECT_LOADS_WITHOUT_COUNT'} />
                </Typography>
                <Stack my={2}>
                    <InputWithCustomPlaceholder
                        id="searchKey"
                        value={searchKey ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            setSearchKey(e.target.value);
                        }}
                        placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                        size="small"
                        maxWidth={'100%'}
                        InputProps={{
                            startAdornment: <Search id="search" sx={{ mr: '5px', color: '#727e84' }} />,
                        }}
                    />
                </Stack>
                <Box className="bg-white overflow-y-scroll" sx={{ height: 'calc(100vh - 480px)' }}>
                    <List disablePadding>
                        {isLoading || isFetching ? (
                            <ListSkeleton total={8} />
                        ) : (
                            filteredLoads.map((item: any) => (
                                <InfoListItem
                                    title={''}
                                    key={item?.id ?? item?.loadId}
                                    divider="full"
                                    leftComponent={
                                        <Stack flexDirection={'row'} alignItems={'center'}>
                                            <Box sx={loadNumberStyle}>{getDisplayLoadNumber(item)}</Box>
                                            <Typography fontSize={16} fontWeight={600} color="#424e54" pl={1}>
                                                {item?.loadName ?? ''}
                                            </Typography>
                                        </Stack>
                                    }
                                    rightComponent={
                                        <Checkbox
                                            edge="end"
                                            onChange={() => handleChannelSelection(item)}
                                            checked={handleChecked(item)}
                                            disabled={isExistingLoad(item)}
                                        />
                                    }
                                    hidePadding
                                />
                            ))
                        )}
                    </List>
                </Box>
            </Box>
        </Box>
    );
};
